import type { DiGraph } from "../graph/digraph.js";
import { undirectedHops } from "../graph/dagReach.js";

/**
 * Distractor selection strategy:
 *  - `near`  → most confusable concepts first (1–2 hops). Good for **hard** items.
 *  - `far`   → distant concepts first (3+ hops). Good for **easy** items.
 *  - `mixed` → split the count: half nearest, half farthest. Good default.
 */
export type DistractorMode = "near" | "far" | "mixed";

/**
 * Picks `count` other concept nodes to serve as plausible wrong answers using `mode`.
 * Disconnected concepts are treated as “very far” (`hops = 99`).
 */
export function pickDistractorsByMode(
  g: DiGraph,
  correct: string,
  count: number,
  mode: DistractorMode,
  rng: () => number
): string[] {
  const pool = g.nodes.filter((c) => c !== correct);
  if (count >= pool.length) return shuffle(pool, rng).slice(0, count);

  const scored = pool.map((c) => {
    const h = undirectedHops(g, correct, c);
    const d = h === Number.POSITIVE_INFINITY ? 99 : h;
    return { c, d, tie: rng() };
  });

  if (mode === "near") {
    scored.sort((a, b) => a.d - b.d || a.tie - b.tie);
    return scored.slice(0, count).map((x) => x.c);
  }
  if (mode === "far") {
    scored.sort((a, b) => b.d - a.d || a.tie - b.tie);
    return scored.slice(0, count).map((x) => x.c);
  }
  // mixed
  const nearHalf = Math.ceil(count / 2);
  const farHalf = count - nearHalf;
  const nearSorted = [...scored].sort((a, b) => a.d - b.d || a.tie - b.tie);
  const near = nearSorted.slice(0, nearHalf).map((x) => x.c);
  const remaining = scored.filter((x) => !near.includes(x.c));
  remaining.sort((a, b) => b.d - a.d || a.tie - b.tie);
  const far = remaining.slice(0, farHalf).map((x) => x.c);
  return shuffle([...near, ...far], rng);
}

/**
 * Maps an Elo-ish difficulty to a distractor mode.
 * - `< 1200` → `far`   (easier, options look very different)
 * - `1200–1500` → `mixed`
 * - `> 1500` → `near`  (harder, options look confusable)
 */
export function distractorModeForRating(rating: number): DistractorMode {
  if (rating < 1200) return "far";
  if (rating > 1500) return "near";
  return "mixed";
}

/**
 * Back-compat (pre-A.7): same as `pickDistractorsByMode(..., "near", ...)`.
 */
export function pickDistractorConcepts(
  g: DiGraph,
  correct: string,
  count: number,
  rng: () => number
): string[] {
  return pickDistractorsByMode(g, correct, count, "near", rng);
}

function shuffle<T>(a: T[], rng: () => number): T[] {
  const out = [...a];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [out[i], out[j]] = [out[j]!, out[i]!];
  }
  return out;
}
